import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Home, Mail, Calendar, Users, MapPin, Phone, Download, Printer, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { hotelInfo } from '../mockData';

const BookingConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const bookingRef = searchParams.get('ref');
  const [booking, setBooking] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  useEffect(() => {
    // Try to get booking from localStorage
    const storedBookings = JSON.parse(localStorage.getItem('bookings') || '[]');
    const foundBooking = storedBookings.find(b => 
      b.bookingReference === bookingRef || b.booking_reference === bookingRef
    );
    if (foundBooking) {
      setBooking(foundBooking);
    }
  }, [bookingRef]);

  const getCurrencySymbol = (curr) => {
    const symbols = { USD: '$', ZAR: 'R', GBP: '£', EUR: '€' };
    return symbols[curr] || '$';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateNights = () => {
    if (!booking) return 0;
    // Use stored nights if available
    if (booking.nights) return booking.nights;
    
    const checkIn = booking.checkIn || booking.check_in;
    const checkOut = booking.checkOut || booking.check_out;
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  return (
    <div
      className="min-h-screen px-4 py-12"
      style={{ backgroundColor: 'var(--bg-primary)', paddingTop: '100px' }}
      data-testid="booking-confirmation-page"
    >
      <div className="container max-w-4xl">
        {/* Success Message */}
        <div
          className="p-12 text-center mb-8"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)'
          }}
        >
          <div
            className="inline-flex items-center justify-center w-20 h-20 mb-6"
            style={{
              backgroundColor: 'var(--status-success)',
              borderRadius: '50%'
            }}
          >
            <CheckCircle size={40} style={{ color: 'var(--bg-primary)' }} />
          </div>

          <h1 className="hero-medium mb-4">Booking Confirmed!</h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Your reservation has been successfully confirmed.
          </p>

          <div
            className="p-6 mb-8 inline-block"
            style={{
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-light)'
            }}
          >
            <p className="body-small mb-2" style={{ color: 'var(--text-secondary)' }}>
              Booking Reference
            </p>
            <p className="heading-2" data-testid="booking-reference">{bookingRef}</p>
          </div>

          <p className="body-regular mb-8" style={{ color: 'var(--text-secondary)' }}>
            A confirmation email has been sent to your email address with all the booking details.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard">
              <Button 
                className="flex items-center gap-2"
                style={{ backgroundColor: 'var(--olive-primary)', color: 'white', borderRadius: 0 }}
              >
                <CreditCard size={16} />
                Pay Now
              </Button>
            </Link>
            <Button 
              onClick={() => setShowEmailPreview(!showEmailPreview)}
              className="btn-primary flex items-center gap-2"
              data-testid="preview-email-btn"
            >
              <Mail size={16} />
              {showEmailPreview ? 'Hide' : 'Preview'} Confirmation Email
            </Button>
            <Link to="/dashboard">
              <Button variant="outline" style={{ borderRadius: 0 }}>View My Bookings</Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="flex items-center gap-2" style={{ borderRadius: 0 }}>
                <Home size={16} />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>

        {/* Email Preview Section */}
        {showEmailPreview && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-2">Email Preview</h2>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-2"
                  style={{ borderRadius: 0 }}
                  onClick={() => window.print()}
                >
                  <Printer size={14} />
                  Print
                </Button>
              </div>
            </div>
            
            {/* Email Container */}
            <div
              className="shadow-lg"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid var(--border-light)',
                maxWidth: '600px',
                margin: '0 auto'
              }}
              data-testid="email-preview"
            >
              {/* Email Header */}
              <div
                style={{
                  backgroundColor: '#5F6F52',
                  padding: '32px 24px',
                  textAlign: 'center'
                }}
              >
                <h1 style={{ 
                  color: '#ffffff', 
                  fontFamily: "'Crimson Text', serif",
                  fontSize: '28px',
                  fontWeight: 400,
                  margin: 0
                }}>
                  OlivOak Villa
                </h1>
                <p style={{ 
                  color: 'rgba(255,255,255,0.8)', 
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '12px',
                  letterSpacing: '2px',
                  marginTop: '8px',
                  textTransform: 'uppercase'
                }}>
                  Booking Confirmation
                </p>
              </div>

              {/* Email Body */}
              <div style={{ padding: '32px 24px' }}>
                {/* Greeting */}
                <p style={{ 
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '16px',
                  color: '#1a1a1a',
                  marginBottom: '24px'
                }}>
                  Dear {booking?.firstName || 'Guest'} {booking?.lastName || ''},
                </p>
                
                <p style={{ 
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  color: '#666',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Thank you for choosing OlivOak Villa. We are delighted to confirm your reservation. 
                  Please find your booking details below.
                </p>

                {/* Booking Reference Box */}
                <div style={{
                  backgroundColor: '#f8f7f4',
                  border: '2px solid #5F6F52',
                  padding: '20px',
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <p style={{ 
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '12px',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '8px'
                  }}>
                    Your Booking Reference
                  </p>
                  <p style={{ 
                    fontFamily: "'Crimson Text', serif",
                    fontSize: '32px',
                    color: '#5F6F52',
                    fontWeight: 600,
                    margin: 0
                  }}>
                    {bookingRef}
                  </p>
                </div>

                {/* Reservation Details */}
                <h2 style={{
                  fontFamily: "'Crimson Text', serif",
                  fontSize: '20px',
                  color: '#1a1a1a',
                  borderBottom: '1px solid #e5e5e5',
                  paddingBottom: '12px',
                  marginBottom: '20px'
                }}>
                  Reservation Details
                </h2>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                  <tbody>
                    <tr>
                      <td style={{ 
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#666',
                        width: '40%'
                      }}>
                        <Calendar size={14} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                        Check-In
                      </td>
                      <td style={{ 
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#1a1a1a',
                        fontWeight: 500
                      }}>
                        {formatDate(booking?.checkIn || booking?.check_in || new Date())}
                        <span style={{ color: '#666', fontWeight: 400 }}> (from 2:00 PM)</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ 
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        <Calendar size={14} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                        Check-Out
                      </td>
                      <td style={{ 
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#1a1a1a',
                        fontWeight: 500
                      }}>
                        {formatDate(booking?.checkOut || booking?.check_out || new Date())}
                        <span style={{ color: '#666', fontWeight: 400 }}> (by 11:00 AM)</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ 
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        Room Type
                      </td>
                      <td style={{ 
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#1a1a1a',
                        fontWeight: 500
                      }}>
                        {booking?.roomName || booking?.room_name || 'Deluxe Suite'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ 
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        <Users size={14} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                        Guests
                      </td>
                      <td style={{ 
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#1a1a1a',
                        fontWeight: 500
                      }}>
                        {booking?.guests || 2} {(booking?.guests || 2) === 1 ? 'Guest' : 'Guests'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ 
                        padding: '12px 0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        Duration
                      </td>
                      <td style={{ 
                        padding: '12px 0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#1a1a1a',
                        fontWeight: 500
                      }}>
                        {calculateNights()} {calculateNights() === 1 ? 'Night' : 'Nights'}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Payment Summary */}
                <h2 style={{
                  fontFamily: "'Crimson Text', serif",
                  fontSize: '20px',
                  color: '#1a1a1a',
                  borderBottom: '1px solid #e5e5e5',
                  paddingBottom: '12px',
                  marginBottom: '20px'
                }}>
                  Payment Summary
                </h2>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                  <tbody>
                    <tr>
                      <td style={{ 
                        padding: '8px 0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        Room ({calculateNights()} nights)
                      </td>
                      <td style={{ 
                        padding: '8px 0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#1a1a1a',
                        textAlign: 'right'
                      }}>
                        {getCurrencySymbol(booking?.currency || 'USD')}{(booking?.subtotal || 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ 
                        padding: '8px 0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#666'
                      }}>
                        ZTA Tax (2%)
                      </td>
                      <td style={{ 
                        padding: '8px 0',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '14px',
                        color: '#1a1a1a',
                        textAlign: 'right'
                      }}>
                        {getCurrencySymbol(booking?.currency || 'USD')}{(booking?.tax || 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ 
                        padding: '16px 0 8px 0',
                        borderTop: '2px solid #5F6F52',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '16px',
                        color: '#1a1a1a',
                        fontWeight: 600
                      }}>
                        Total
                      </td>
                      <td style={{ 
                        padding: '16px 0 8px 0',
                        borderTop: '2px solid #5F6F52',
                        fontFamily: "'Crimson Text', serif",
                        fontSize: '24px',
                        color: '#5F6F52',
                        textAlign: 'right',
                        fontWeight: 600
                      }}>
                        {getCurrencySymbol(booking?.currency || 'USD')}{(booking?.total || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Special Requests */}
                {booking?.specialRequests && (
                  <>
                    <h2 style={{
                      fontFamily: "'Crimson Text', serif",
                      fontSize: '20px',
                      color: '#1a1a1a',
                      borderBottom: '1px solid #e5e5e5',
                      paddingBottom: '12px',
                      marginBottom: '20px'
                    }}>
                      Special Requests
                    </h2>
                    <p style={{ 
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '14px',
                      color: '#666',
                      fontStyle: 'italic',
                      marginBottom: '24px',
                      padding: '16px',
                      backgroundColor: '#f8f7f4'
                    }}>
                      "{booking.specialRequests}"
                    </p>
                  </>
                )}

                {/* Hotel Information */}
                <div style={{
                  backgroundColor: '#f8f7f4',
                  padding: '24px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    fontFamily: "'Crimson Text', serif",
                    fontSize: '18px',
                    color: '#1a1a1a',
                    marginBottom: '16px'
                  }}>
                    Hotel Information
                  </h3>
                  <p style={{ 
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <MapPin size={14} />
                    {hotelInfo.location.address}
                  </p>
                  <p style={{ 
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Phone size={14} />
                    {hotelInfo.contact.phone}
                  </p>
                  <p style={{ 
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Mail size={14} />
                    {hotelInfo.contact.email}
                  </p>
                </div>

                {/* Important Notes */}
                <div style={{
                  borderLeft: '4px solid #5F6F52',
                  padding: '16px',
                  backgroundColor: '#fafafa',
                  marginBottom: '24px'
                }}>
                  <h4 style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1a1a1a',
                    marginBottom: '12px'
                  }}>
                    Important Information
                  </h4>
                  <ul style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '13px',
                    color: '#666',
                    margin: 0,
                    paddingLeft: '20px',
                    lineHeight: '1.8'
                  }}>
                    <li>Check-in time: 2:00 PM onwards</li>
                    <li>Check-out time: 11:00 AM</li>
                    <li>Please present this confirmation upon arrival</li>
                    <li>Valid ID/Passport required at check-in</li>
                    <li>Free cancellation up to 48 hours before check-in</li>
                  </ul>
                </div>

                {/* Closing */}
                <p style={{ 
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  color: '#666',
                  lineHeight: '1.6',
                  marginBottom: '16px'
                }}>
                  We look forward to welcoming you to OlivOak Villa. Should you have any questions 
                  or require assistance, please don't hesitate to contact us.
                </p>
                <p style={{ 
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  color: '#1a1a1a',
                  marginBottom: '4px'
                }}>
                  Warm regards,
                </p>
                <p style={{ 
                  fontFamily: "'Crimson Text', serif",
                  fontSize: '16px',
                  color: '#5F6F52',
                  fontWeight: 500
                }}>
                  The OlivOak Villa Team
                </p>
              </div>

              {/* Email Footer */}
              <div style={{
                backgroundColor: '#1a1a1a',
                padding: '24px',
                textAlign: 'center'
              }}>
                <p style={{ 
                  fontFamily: "'Crimson Text', serif",
                  fontSize: '18px',
                  color: '#ffffff',
                  marginBottom: '8px'
                }}>
                  OlivOak Villa
                </p>
                <p style={{ 
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.6)',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '16px'
                }}>
                  Refined Comfort. Tranquil Living.
                </p>
                <p style={{ 
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.4)'
                }}>
                  © {new Date().getFullYear()} OlivOak Villa. All rights reserved.
                </p>
              </div>
            </div>

            {/* Mock Email Notice */}
            <div 
              className="mt-4 p-4 text-center"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px dashed var(--border-medium)'
              }}
            >
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                <strong>Note:</strong> This is a preview of the confirmation email. 
                In production, this email will be sent via Resend to your email address.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
